import React from 'react';

import RecordingFeesData, { FeeInfoIcon } from '@sharedCR/loanApplication/RecordingFeesData';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { DISCOUNT_POINTS_FEE, FeeRecordingType } from 'constants/feeTypes';
import { LESection } from 'constants/loanApplication';
import { currencyOptions, ModifiedFieldsMapper } from 'constants/loanFee';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { jsonParse } from 'utils/json';

import { formatNumberToCurrencyString } from './number';
import { naturalSortBy } from './sort';
import { caseInsensitiveIncludes } from './string';

// A folded appraisal-surcharge row (STP-242) keeps the base fee's own amount fields
// unmutated for editing, so its surcharges' amounts must be added back in here —
// otherwise a section total built by summing per-item totals silently drops them
// (the surcharge row itself was removed from the item list it's summed over).
const getAppraisalSurchargesTotalAmount = appraisalSurcharges =>
  _.sumBy(appraisalSurcharges, surcharge =>
    _.sum([
      Number(surcharge.borrowerPaidAtClosingAmount || 0),
      Number(surcharge.borrowerPaidBeforeClosingAmount || 0),
    ]),
  );

export const getTotalAmountFee = i => {
  return i.pendingChangesActionType !== PendingChangesActionType.DELETED
    ? _.sum([
        Number(i.borrowerPaidAtClosingAmount || 0),
        Number(i.borrowerPaidBeforeClosingAmount || 0),
        getAppraisalSurchargesTotalAmount(i.appraisalSurcharges),
      ])
    : 0;
};

export const getSectionEFeesValues = ({ fees, isEdit }) => {
  const {
    [LESection.RECORDING_FEES]: recordingFees = [],
    [LESection.TRANSFER_TAXES]: transferTaxes = [],
  } = _.groupBy(fees, 'leSection');

  const deedTotal = _.sumBy(recordingFees, i =>
    _.get(i, 'recordingType.value', i.recordingType) === FeeRecordingType.DEED
      ? getTotalAmountFee(i)
      : 0,
  );

  const mortgageTotal = _.sumBy(recordingFees, i =>
    _.get(i, 'recordingType.value', i.recordingType) === FeeRecordingType.MORTGAGE
      ? getTotalAmountFee(i)
      : 0,
  );

  const getRecodingTotalAmount = prop =>
    _.sumBy(recordingFees, i =>
      i.pendingChangesActionType !== PendingChangesActionType.DELETED ? Number(i[prop] || 0) : 0,
    );

  const recordingTotalModifiedFields = _.uniq(
    recordingFees
      .map(i =>
        [PendingChangesActionType.DELETED, PendingChangesActionType.ADDED].includes(
          i.pendingChangesActionType,
        )
          ? _.map(i, (val, key) => (val ? key : null)).filter(Boolean)
          : i.modifiedFields,
      )
      .flat(),
  );

  const recordingFeesByType = _.groupBy(recordingFees, fee =>
    _.get(fee, 'recordingType.value', fee.recordingType),
  );

  const sortedRecordingFees = [FeeRecordingType.DEED, FeeRecordingType.MORTGAGE]
    .flatMap(type => naturalSortBy(recordingFeesByType[type] || [], 'feeName'))
    .concat(
      Object.values(
        _.omit(recordingFeesByType, [FeeRecordingType.DEED, FeeRecordingType.MORTGAGE]),
      ).flatMap(fees => naturalSortBy(fees, 'feeName')),
    );

  const sortedTransferTaxes = naturalSortBy(transferTaxes, 'feeName');

  return {
    recordingFees: sortedRecordingFees,
    transferTaxes: sortedTransferTaxes,

    recordingFeesTotal: [
      {
        name: (
          <span className="flex items-center gap-2">
            {`Recording Fees. Deed:
          ${formatNumberToCurrencyString(deedTotal, currencyOptions)}
          Mortgage:
          ${formatNumberToCurrencyString(mortgageTotal, currencyOptions)}`}
            {!isEdit && <FeeInfoIcon content={<RecordingFeesData data={sortedRecordingFees} />} />}
          </span>
        ),
        borrowerPaidAtClosingAmount: getRecodingTotalAmount('borrowerPaidAtClosingAmount'),
        borrowerPaidBeforeClosingAmount: getRecodingTotalAmount('borrowerPaidBeforeClosingAmount'),
        sellerPaidBeforeClosingAmount: getRecodingTotalAmount('sellerPaidBeforeClosingAmount'),
        sellerPaidAtClosingAmount: getRecodingTotalAmount('sellerPaidAtClosingAmount'),
        paidByOthersAmount: getRecodingTotalAmount('paidByOthersAmount'),
        modifiedFields: [
          ...recordingTotalModifiedFields,
          !!recordingTotalModifiedFields.length && 'feeTypeName',
        ],
      },
    ],
  };
};

export const isFieldValueChanged = (fee, prop) => {
  const isFeeAdded = fee.pendingChangesActionType === PendingChangesActionType.ADDED;
  const isModifiedValue = _.includes(fee.modifiedFields, ModifiedFieldsMapper[prop] || prop);

  const currentValue = _.get(fee, `${prop}.value`, fee[prop]) || _.get(fee, prop) || '';
  const initialValue =
    _.get(fee, `initialFee.${prop}.value`, fee.initialFee?.[prop]) ||
    _.get(fee, `initialFee.${prop}`) ||
    '';

  const isTempValueChanged = `${currentValue}` !== `${initialValue}`;

  return isModifiedValue || isTempValueChanged || isFeeAdded;
};

export const getPendingChangesActionType = (modifiedFields, field) =>
  (_.includes(modifiedFields, field) && PendingChangesActionType.MODIFIED) || undefined;

export const getPendingChangesInfo = (ref, isTableStructure = true) => {
  const rowSelector = isTableStructure ? 'tr' : 'div';
  const changedCellRowSelector = `${rowSelector}:not(.table-row-pending-${PendingChangesActionType.DELETED}):not(.table-row-pending-${PendingChangesActionType.ADDED}) [data-is-changed="true"]`;
  const deletedRowSelector = `.table-row-pending-${PendingChangesActionType.DELETED}`;
  const addedRowSelector = `.table-row-pending-${PendingChangesActionType.ADDED}`;

  const querySelector = `${changedCellRowSelector}, ${deletedRowSelector}, ${addedRowSelector}`;

  const pendingChangesNumber = ref.current?.querySelectorAll(querySelector)?.length || 0;
  const isTotalAmountPending = !!ref.current?.querySelectorAll(
    `${changedCellRowSelector}[data-is-amount="true"], ${deletedRowSelector}, ${addedRowSelector}`,
  )?.length;

  return { pendingChangesNumber, isTotalAmountPending };
};

export const getSellerAdjustmentViewLabel = ({ name, startDate, endDate }) =>
  [
    name,
    dayjs(startDate).format(COMMON_DATE_FORMAT),
    'to',
    dayjs(endDate).format(COMMON_DATE_FORMAT),
  ].join(' ');

export const getFilteredLoanFeesOptions = ({
  feesOptions,
  isManageDiscountPointsAllowed,
  stringifiedConfigFormFees = '{}',
  leSection,
}) => {
  const formFees = jsonParse(stringifiedConfigFormFees);

  return feesOptions.filter(o => {
    const isPrepaid = leSection === LESection.PREPAIDS;
    const isDiscountPointsFee = caseInsensitiveIncludes(o.name, DISCOUNT_POINTS_FEE);

    if (
      o.isCustomDisabled ||
      isPrepaid ||
      o.isDeleted ||
      (!isManageDiscountPointsAllowed && isDiscountPointsFee) ||
      (leSection && leSection !== o.leSection)
    ) {
      return false;
    }

    return !formFees[o.value];
  });
};

export const FEE_TYPE_OVERRIDABLE_FIELDS = ['countTowardsApr', 'allowAmountEditing'];

export const getFeeTypeOverridableFields = (feeTypeId, feeTypes) => {
  const feeType = feeTypes?.[feeTypeId];

  if (_.isNil(feeType)) {
    return {};
  }

  return _.pick(feeType, FEE_TYPE_OVERRIDABLE_FIELDS);
};

export const enrichFeeWithFeeTypeFields = (fee, feeTypes) => ({
  ...fee,
  ...getFeeTypeOverridableFields(fee?.feeTypeId, feeTypes),
});

export const getIsFeeAmountEditingDisallowed = (feeTypeId, feeTypes) =>
  feeTypes?.[feeTypeId]?.allowAmountEditing === false;
